/**
 * `decodeLightmapGIData`: a LightmapGIData's property bag as the renderer reads it, from a
 * `.lmbake`, a `.tres` or a scene's sub-resource alike (ADR-0031). Each property's setter is
 * in `scene/3d/lightmap_gi.cpp`.
 */
import { floatOr, parseOptionalAabb, parseOptionalRect2 } from '../../parser/valueParsers';
import { EMPTY_AABB } from '../../godot/math/aabb';
import { splitTopLevel } from '../../godot/string/string';
import { slotInt } from '../../godot/variant/intSlots';
import { boolSlotValue } from '../../godot/variant/variantBool';
import { arrayLiteralBody, dictCallField, dictNumberField, nodePathLiteral, } from '../../godot/variant/variantParser';
import { LIGHTMAP_GI_DATA_DEFAULTS, LIGHTMAP_USER_FIELDS } from '../../godot/scene/lightmapGi';
import { acceptedPointCount } from './captureProbes';
import { resolveExtResourcePath } from '../SubResourceResolver';
/** `externalResources` are the pools the data's own `ExtResource` references resolve against. */
export function decodeLightmapGIData(properties, externalResources) {
    const usesSphericalHarmonics = boolSlotValue(properties.uses_spherical_harmonics) ?? LIGHTMAP_GI_DATA_DEFAULTS.usesSphericalHarmonics;
    const usesPackedDirectional = boolSlotValue(properties._uses_packed_directional) ?? LIGHTMAP_GI_DATA_DEFAULTS.usesPackedDirectional;
    // A LightmapGI refuses directional data in the unpacked format older bakes wrote (`:1509-1513`).
    const isAssignable = !usesSphericalHarmonics || usesPackedDirectional;
    const pointCount = acceptedCount(properties.probe_data);
    return {
        // An older bake spells it `light_textures`, which the setter hands on (`:317-318`).
        lightmapTextures: texturePaths(properties.lightmap_textures ?? properties.light_textures, externalResources),
        shadowmaskTextures: texturePaths(properties.shadowmask_textures, externalResources),
        usesSphericalHarmonics,
        users: isAssignable ? lightmapUsers(properties.user_data) : [],
        bounds: captureBounds(properties.probe_data, pointCount),
        bakedExposure: bakedExposure(properties.probe_data, pointCount),
    };
}
/** The points `set_capture_data` accepts, null for refused data or none. */
function acceptedCount(probeData) {
    return probeData === undefined ? null : acceptedPointCount(probeData);
}
const readBounds = dictCallField('bounds', 'AABB');
const BAKED_EXPOSURE_FIELD = dictNumberField('baked_exposure');
/**
 * The bounds `probe_data` gives the capture. `set_capture_data` keeps them only with probe points
 * (`:223-234`), and the data starts with none (`lightmap_gi.h:69`).
 */
function captureBounds(probeData, pointCount) {
    if (probeData === undefined || !pointCount)
        return EMPTY_AABB;
    const bounds = readBounds(probeData);
    return (bounds !== null && parseOptionalAabb(`AABB(${bounds})`, 'LightmapGIData probe_data')) || EMPTY_AABB;
}
/**
 * The exposure the bake ran at. `set_capture_data` stores it whole, with or without points
 * (`lightmap_gi.cpp:236-237`), once it has accepted the data.
 */
function bakedExposure(probeData, pointCount) {
    const fallback = LIGHTMAP_GI_DATA_DEFAULTS.bakedExposure;
    if (probeData === undefined || pointCount === null)
        return fallback;
    return floatOr(BAKED_EXPOSURE_FIELD.exec(probeData)?.[1], fallback, 'LightmapGIData baked_exposure');
}
/** The path of each external TextureLayered the array names, in order. */
function texturePaths(raw, externalResources) {
    const body = raw === undefined ? null : arrayLiteralBody(raw);
    if (body === null)
        return [];
    return splitTopLevel(body).flatMap((element) => resolveExtResourcePath(element.trim(), externalResources) ?? []);
}
/**
 * Each group of four elements as a user. An array that is not whole groups keeps none, as
 * `_set_user_data` returns before it clears (`lightmap_gi.cpp:84`), and so does a group whose
 * element does not convert.
 */
function lightmapUsers(raw) {
    const body = raw === undefined ? null : arrayLiteralBody(raw);
    if (body === null)
        return [];
    const elements = splitTopLevel(body);
    if (elements.length % LIGHTMAP_USER_FIELDS !== 0)
        return [];
    const users = [];
    for (let i = 0; i < elements.length; i += LIGHTMAP_USER_FIELDS) {
        const user = lightmapUser(elements.slice(i, i + LIGHTMAP_USER_FIELDS));
        if (!user)
            return [];
        users.push(user);
    }
    return users;
}
function lightmapUser([pathText, rectText, sliceText, subInstanceText]) {
    const path = nodePathLiteral(pathText);
    const uvScale = parseOptionalRect2(rectText, 'LightmapGIData user_data');
    const sliceIndex = slotInt(sliceText);
    const subInstance = slotInt(subInstanceText);
    if (path === null || !uvScale || sliceIndex === null || subInstance === null)
        return null;
    return { path, uvScale, sliceIndex, subInstance };
}
//# sourceMappingURL=decode.js.map